import React from 'react'
import { ColDef, ICellRendererParams } from '@ag-grid-community/core'
import { StatusTag } from '../components/StatusTag'
import { eventDataWithReasonFirst } from '../deriveTaskReason'
import { PartnerBenefitEvent, TaskStatus, PartnerSyncStatus } from '../types'

const formatDateTime = (timestamp: string): string => {
  if (!timestamp) return ''
  return new Date(timestamp).toLocaleString()
}

const EventDataRenderer: React.FC<ICellRendererParams<PartnerBenefitEvent>> = ({
  data,
  value,
}) => {
  if (!value) return null

  const displayed = eventDataWithReasonFirst(data?.eventType ?? '', value)

  return (
    <pre
      style={{
        padding: '4px',
        fontSize: '11px',
        lineHeight: '1.3',
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {JSON.stringify(displayed, null, 2)}
    </pre>
  )
}

export const getMainColumnDefs = (): ColDef[] => [
  {
    headerName: 'Partner',
    field: 'partnerBenefitName',
    cellRenderer: 'agGroupCellRenderer',
    pinned: 'left',
    flex: 1,
    minWidth: 120,
    filter: false,
    suppressHeaderMenuButton: true,
  },
  {
    headerName: 'Task ID',
    field: 'id',
    flex: 1,
    maxWidth: 100,
    filter: false,
    suppressHeaderMenuButton: true,
  },
  {
    headerName: 'Sync Status',
    field: 'taskStatus',
    filter: 'agSetColumnFilter',
    minWidth: 110,
    maxWidth: 120,
    filterParams: {
      values: Object.values(TaskStatus),
    },
    cellRenderer: (params: { value: TaskStatus }) => (
      <StatusTag status={params.value} />
    ),
  },
  {
    headerName: 'Partner Sync Status',
    field: 'partnerSyncStatus',
    filter: 'agSetColumnFilter',
    minWidth: 110,
    maxWidth: 120,
    filterParams: {
      values: Object.values(PartnerSyncStatus),
    },
    cellRenderer: (params: { value: PartnerSyncStatus }) => (
      <StatusTag status={params.value} />
    ),
  },
  {
    headerName: 'First Name',
    field: 'person.firstName',
    colId: 'personFirstName',
    sortable: false,
    filter: 'agTextColumnFilter',
    suppressHeaderFilterButton: true,
    minWidth: 200,
    filterParams: {
      filterOptions: ['contains'],
      suppressAndOrCondition: true,
      suppressFilterButton: true,
      filterType: 'text',
    },
  },
  {
    headerName: 'Last Name',
    field: 'person.lastName',
    colId: 'personLastName',
    sortable: false,
    filter: 'agTextColumnFilter',
    minWidth: 200,
    filterParams: {
      filterOptions: ['contains'],
      suppressAndOrCondition: true,
    },
  },
  {
    headerName: 'Company Name',
    field: 'client.companyName',
    colId: 'clientCompanyName',
    filter: false,
    minWidth: 200,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  {
    headerName: 'Sync Request Reason',
    field: 'syncRequestReason',
    minWidth: 100,
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  {
    headerName: 'Created At',
    field: 'createdTimestamp',
    minWidth: 150,
    filter: false,
    suppressHeaderMenuButton: true,
    valueFormatter: (params) => formatDateTime(params.value),
  },
  {
    headerName: 'Employee public key',
    field: 'employeePublicKey',
    minWidth: 180,
    filter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
]

export const getDetailColumnDefs = (): ColDef[] => [
  {
    headerName: 'Event ID',
    field: 'id',
    minWidth: 100,
    maxWidth: 150,
  },
  {
    headerName: 'Event Type',
    field: 'eventType',
    minWidth: 120,
  },
  {
    headerName: 'Event Data',
    field: 'eventData',
    flex: 1,
    wrapText: true,
    autoHeight: true,
    resizable: true,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    cellRenderer: EventDataRenderer,
  },
  {
    headerName: 'Created At',
    field: 'createdTimestamp',
    minWidth: 150,
    sort: 'desc',
    valueFormatter: (params) => formatDateTime(params.value),
  },
]
